import React, { useCallback, useEffect, useRef, useState } from "react";
import { GoogleMap, Polygon, Polyline, MarkerF } from "@react-google-maps/api";
import WithGoogleMapsLoader from "../../../Components/WithGoogleMapsLoader";
import DrawToolbar from "./DrawToolbar";
import AreaSearchBox from "./AreaSearchBox";
import AreaNamePopover from "./AreaNamePopover";
import {
  toGoogleMapsPath,
  pathToGeoJson,
  computeCentroid,
  boxFromViewport,
  boxAround,
  areaMapPath,
} from "../utils/geo";
import { colorForIndex } from "../utils/colors";

export const CALABAR_CENTER = { lat: 4.9774587, lng: 8.341548399999999 };

// Computed lazily (not at module scope) since window.google.maps.SymbolPath
// only exists once the Maps script has finished loading via
// WithGoogleMapsLoader, which wraps this component's export below.
const getVertexIcon = () => ({
  path: window.google.maps.SymbolPath.CIRCLE,
  scale: 5,
  fillColor: "#ffffff",
  fillOpacity: 1,
  strokeColor: "#2F80ED",
  strokeWeight: 2.5,
});

const detachPolygonFromMap = (polygon) => {
  if (!polygon) return;

  polygon.setEditable?.(false);
  polygon.setDraggable?.(false);
  polygon.setVisible?.(false);
  polygon.setMap?.(null);

  if (typeof window !== "undefined" && window.google?.maps?.event) {
    window.google.maps.event.clearInstanceListeners(polygon);
  }
};

const ZoneMapCanvas = ({
  areas,
  activeTool,
  setActiveTool,
  selectedAreaId,
  setSelectedAreaId,
  pendingArea,
  setPendingArea,
  mapStateVersion,
  onResetMapState,
  onAreaBoundaryChange,
  onAreaDeleteRequest,
  onAreaConfirm,
  onMapLoad,
}) => {
  const polygonRefs = useRef(new Map());
  const pendingPolygonRef = useRef(null);
  const pendingPolygonRefs = useRef(new Set());
  const mapInstanceRef = useRef(null);
  const pendingClickTimer = useRef(null);
  const savedDraggingAreaIdsRef = useRef(new Set());
  const onAreaBoundaryChangeRef = useRef(onAreaBoundaryChange);
  // In-progress freehand-drawn points, before the shape is closed into a
  // pendingArea. `DrawingManager` was the natural fit here but Google
  // removed it from the Maps JS API as of v3.65, so vertices are tracked
  // by hand via a debounced click handler instead: a click only commits as
  // a new vertex if a second click doesn't follow within the double-click
  // window, since a genuine double-click's own first click otherwise reads
  // identically to a single click when it happens.
  const [drawingPath, setDrawingPath] = useState([]);
  const [mapReadyVersion, setMapReadyVersion] = useState(0);
  const drawingPathRef = useRef(drawingPath);
  drawingPathRef.current = drawingPath;

  const detachPendingPolygon = useCallback(() => {
    pendingPolygonRefs.current.forEach((polygon) => detachPolygonFromMap(polygon));
    detachPolygonFromMap(pendingPolygonRef.current);
    pendingPolygonRefs.current.clear();
    pendingPolygonRef.current = null;
  }, []);

  const detachSavedPolygons = useCallback(() => {
    polygonRefs.current.forEach((polygon) => detachPolygonFromMap(polygon));
    polygonRefs.current.clear();
    savedDraggingAreaIdsRef.current.clear();
  }, []);

  useEffect(() => {
    onAreaBoundaryChangeRef.current = onAreaBoundaryChange;
  }, [onAreaBoundaryChange]);

  useEffect(() => {
    if (!pendingArea) detachPendingPolygon();
  }, [detachPendingPolygon, pendingArea]);

  useEffect(() => {
    if (pendingClickTimer.current) {
      clearTimeout(pendingClickTimer.current);
      pendingClickTimer.current = null;
    }
    detachPendingPolygon();
    detachSavedPolygons();
    setDrawingPath([]);
  }, [detachPendingPolygon, detachSavedPolygons, mapStateVersion]);

  useEffect(
    () => () => {
      if (pendingClickTimer.current) {
        clearTimeout(pendingClickTimer.current);
      }
      detachPendingPolygon();
      detachSavedPolygons();
    },
    [detachPendingPolygon, detachSavedPolygons],
  );

  const handlePlaceSelected = ({ lat, lng, address, viewport }) => {
    detachPendingPolygon();
    mapInstanceRef.current?.panTo({ lat, lng });
    mapInstanceRef.current?.setZoom(16);
    const path = boxFromViewport(viewport) || boxAround(lat, lng, 250);
    setPendingArea({
      tempId: `temp_${Date.now()}`,
      path,
      boundary: pathToGeoJson(path),
      centroid: computeCentroid(path),
      source: "search",
      address,
    });
    setSelectedAreaId(null);
    setActiveTool(null);
  };

  const finishDrawing = (path) => {
    if (path.length < 3) return;
    detachPendingPolygon();
    setPendingArea({
      tempId: `temp_${Date.now()}`,
      path,
      boundary: pathToGeoJson(path),
      centroid: computeCentroid(path),
      source: "drawn",
    });
    setDrawingPath([]);
    setActiveTool(null);
  };

  const handleMapClick = (e) => {
    if (activeTool !== "draw") return;
    if (pendingClickTimer.current) {
      // A second click arrived inside the double-click window — this is a
      // double-click, so finish the shape using the points already
      // committed, and drop this click rather than adding it as a vertex.
      clearTimeout(pendingClickTimer.current);
      pendingClickTimer.current = null;
      finishDrawing(drawingPathRef.current);
      return;
    }
    const point = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    pendingClickTimer.current = setTimeout(() => {
      pendingClickTimer.current = null;
      setDrawingPath((prev) => [...prev, point]);
    }, 260);
  };

  // Fallback for browsers/automation that dispatch a native `dblclick`
  // without two separate `click` events reaching handleMapClick first.
  const handleMapDblClick = () => {
    if (activeTool !== "draw") return;
    if (pendingClickTimer.current) {
      clearTimeout(pendingClickTimer.current);
      pendingClickTimer.current = null;
    }
    finishDrawing(drawingPathRef.current);
  };

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !selectedAreaId) return;

    const area = areas.find((item) => String(item.id) === String(selectedAreaId));
    if (!area) return;

    const path = areaMapPath(area, 360);
    if (!path.length) return;

    const bounds = new window.google.maps.LatLngBounds();
    path.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds, 80);

    window.setTimeout(() => {
      if (map.getZoom() > 16) map.setZoom(16);
    }, 0);
  }, [areas, mapReadyVersion, selectedAreaId]);

  const getPolygonPath = (polygon) => {
    if (!polygon) return [];
    return polygon
      .getPath()
      .getArray()
      .map((p) => ({ lat: p.lat(), lng: p.lng() }));
  };

  const syncSavedAreaPath = (areaId, { force = false } = {}) => {
    if (!force && savedDraggingAreaIdsRef.current.has(String(areaId))) return;

    const path = getPolygonPath(polygonRefs.current.get(String(areaId)));
    if (!path.length) return;
    onAreaBoundaryChangeRef.current(areaId, {
      boundary: pathToGeoJson(path),
      centroid: computeCentroid(path),
    });
  };

  const getPendingAreaSnapshot = () => {
    if (!pendingArea) return null;

    const path = getPolygonPath(pendingPolygonRef.current);
    if (!path.length) return pendingArea;

    return {
      ...pendingArea,
      path,
      boundary: pathToGeoJson(path),
      centroid: computeCentroid(path),
    };
  };

  const handlePendingPolygonLoad = (polygon) => {
    pendingPolygonRefs.current.add(polygon);
    pendingPolygonRef.current = polygon;
  };

  const handlePendingPolygonUnmount = (polygon) => {
    pendingPolygonRefs.current.delete(polygon);
    if (pendingPolygonRef.current === polygon) {
      pendingPolygonRef.current = null;
    }
    detachPolygonFromMap(polygon);
  };

  const handleDeleteToolClick = () => {
    if (pendingClickTimer.current) {
      clearTimeout(pendingClickTimer.current);
      pendingClickTimer.current = null;
    }

    if (drawingPathRef.current.length) {
      setDrawingPath([]);
      setActiveTool(null);
      return;
    }

    if (pendingArea) {
      onAreaDeleteRequest?.();
      return;
    }

    onAreaDeleteRequest?.(selectedAreaId);
  };

  const handleSavedPolygonLoad = (areaId, polygon) => {
    const normalizedAreaId = String(areaId);
    polygonRefs.current.set(normalizedAreaId, polygon);
  };

  const handleSavedPolygonUnmount = (areaId, polygon) => {
    const normalizedAreaId = String(areaId);
    if (polygonRefs.current.get(normalizedAreaId) === polygon) {
      polygonRefs.current.delete(normalizedAreaId);
    }
    detachPolygonFromMap(polygon);
  };

  const handlePendingConfirm = (name) => {
    const snapshot = getPendingAreaSnapshot();
    detachPendingPolygon();
    onAreaConfirm(name, snapshot);
  };

  const handlePendingCancel = () => {
    detachPendingPolygon();
    setPendingArea(null);
  };

  return (
    <div className='relative w-full h-full bg-[#EAE6D4]'>
      <GoogleMap
        key={mapStateVersion}
        mapContainerClassName='w-full h-full'
        center={CALABAR_CENTER}
        zoom={13}
        onLoad={(map) => {
          mapInstanceRef.current = map;
          setMapReadyVersion((version) => version + 1);
          // The map container's flex-based size can still be settling when
          // this fires, which can leave Google Maps thinking it has a 0x0
          // viewport and never issue tile requests. Nudging it once layout
          // has flushed forces it to re-measure and start loading tiles.
          setTimeout(() => {
            window.google.maps.event.trigger(map, "resize");
            map.setCenter(CALABAR_CENTER);
          }, 0);
          onMapLoad?.(map);
        }}
        onClick={handleMapClick}
        onDblClick={handleMapDblClick}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          disableDoubleClickZoom: true,
          clickableIcons: false,
          draggableCursor: activeTool === "draw" ? "crosshair" : undefined,
        }}
      >
        {drawingPath.length > 0 && (
          <>
            <Polyline
              path={drawingPath}
              options={{
                strokeColor: "#2F80ED",
                strokeWeight: 2.5,
                clickable: false,
              }}
            />
            {drawingPath.map((point, i) => (
              <MarkerF key={i} position={point} icon={getVertexIcon()} clickable={false} />
            ))}
          </>
        )}

        {areas.map((area, index) => {
          const areaId = String(area.id);
          const selected = selectedAreaId === areaId;
          const areaColor = colorForIndex(index);
          const path = selected
            ? areaMapPath(area, 360)
            : toGoogleMapsPath(area.boundary);

          if (!path.length) return null;

          return (
            <Polygon
              key={areaId}
              paths={path}
              onLoad={(polygon) => handleSavedPolygonLoad(areaId, polygon)}
              onUnmount={(polygon) => handleSavedPolygonUnmount(areaId, polygon)}
              options={{
                fillColor: areaColor,
                fillOpacity: selected ? 0.38 : 0.32,
                strokeColor: areaColor,
                strokeWeight: selected ? 3 : 2,
                editable: activeTool !== "delete" && selected,
                draggable: activeTool !== "delete" && selected,
                clickable: true,
                zIndex: selected ? 2 : 1,
              }}
              onClick={() => {
                if (activeTool === "delete") {
                  onAreaDeleteRequest?.(areaId);
                  return;
                }
                if (activeTool !== "draw") {
                  // Selecting a saved area to edit it should not leave a
                  // stray, unconfirmed pending shape frozen on the map —
                  // that pending draft was never saved, so it's disposable
                  // the moment focus moves to a different area.
                  if (pendingArea) {
                    detachPendingPolygon();
                    setPendingArea(null);
                  }
                  setSelectedAreaId(areaId);
                }
              }}
              onMouseUp={() => selected && syncSavedAreaPath(areaId)}
              onDragStart={() => {
                savedDraggingAreaIdsRef.current.add(areaId);
              }}
              onDragEnd={() => {
                savedDraggingAreaIdsRef.current.delete(areaId);
                selected && syncSavedAreaPath(areaId, { force: true });
              }}
            />
          );
        })}

        {pendingArea && (
          <Polygon
            key={pendingArea.tempId}
            paths={pendingArea.path}
            onLoad={handlePendingPolygonLoad}
            onUnmount={handlePendingPolygonUnmount}
            options={{
              fillColor: "#2F80ED",
              fillOpacity: 0.22,
              strokeColor: "#2F80ED",
              strokeWeight: 2.5,
              strokeOpacity: 0.9,
              editable: true,
              draggable: true,
              clickable: true,
            }}
            onClick={() => {
              if (activeTool === "delete") {
                onAreaDeleteRequest?.();
              }
            }}
          />
        )}
      </GoogleMap>

      <AreaNamePopover
        pendingArea={pendingArea}
        onConfirm={handlePendingConfirm}
        onCancel={handlePendingCancel}
      />

      <div className='absolute top-5 left-5 z-10 flex flex-col items-start gap-2.5'>
        <AreaSearchBox onPlaceSelected={handlePlaceSelected} />
        <DrawToolbar
          activeTool={activeTool}
          setActiveTool={(tool) => {
            if (pendingClickTimer.current) {
              clearTimeout(pendingClickTimer.current);
              pendingClickTimer.current = null;
            }
            setDrawingPath([]);
            setActiveTool(tool);
          }}
          onDeleteClick={handleDeleteToolClick}
          onResetClick={onResetMapState}
        />
        {activeTool === "draw" && (
          <div className='bg-primaryBlack text-white text-xs rounded-md px-3 py-2 shadow-md'>
            Click to add point, double-click to finish
          </div>
        )}
      </div>
    </div>
  );
};

export default WithGoogleMapsLoader(ZoneMapCanvas);
